// Inequality dimension — a seven-measure index table (#259).
//
// Replaces the three-bar echarts chart + Gini HeadlineMetric. Each measure is a
// row: name + (i) explainer | absolute post-reform level (change in brackets) |
// a %-change bar on a shared scale. Tom's spec (issue #259). The collapsed
// 6-panel homepage metric (Gini) is unaffected — it reads summary_stats, not
// this component.

import type { EngineOutput, InequalityMetrics } from "../../lib/types";
import { IndexTable, type IndexRow } from "./index_table";

interface Props {
  engineOutput: EngineOutput;
  baselineOutput: EngineOutput | null;
  isModified: boolean;
}

// "+1.23" / "−1.23" with U+2212 minus, matching the rest of the UI.
const signed = (value: number, decimals: number): string =>
  `${value > 0 ? "+" : "−"}${Math.abs(value).toFixed(decimals)}`;

// Ratios (S80/S20, P-ratios): level "x.xx", Δ "+x.xx".
const ratioFmt = {
  formatLevel: (n: number) => n.toFixed(2),
  formatDelta: (d: number) => signed(d, 2),
};

// Fractions shown as percentages (CV, decile shares): level "xx.x%", Δ in
// percentage points "+x.x pp".
const pctFmt = {
  formatLevel: (n: number) => `${(n * 100).toFixed(1)}%`,
  formatDelta: (d: number) => `${signed(d * 100, 1)} pp`,
};

export function InequalityIndices({
  engineOutput,
  baselineOutput,
  isModified,
}: Props): JSX.Element {
  const r = engineOutput.inequality_metrics;
  const b: InequalityMetrics = baselineOutput?.inequality_metrics ?? r;

  const rows: IndexRow[] = [
    {
      key: "gini",
      name: "Gini coefficient",
      description:
        "The most common measure of income inequality, from 0 (everyone equal) to 1 (one household holds everything). Lower is more equal.",
      reform: r.gini,
      baseline: b.gini,
      goodWhen: "decreasing",
      formatLevel: (n) => n.toFixed(3),
      formatDelta: (d) => signed(d, 3),
    },
    {
      key: "cv",
      name: "Coefficient of variation",
      description:
        "The standard deviation of income divided by the mean — a scale-free measure of how spread out incomes are. Lower is more equal.",
      reform: r.cv,
      baseline: b.cv,
      goodWhen: "decreasing",
      ...pctFmt,
    },
    {
      key: "top_decile_share",
      name: "Top decile income share",
      description:
        "Share of all income going to the highest-income 10% of people. Lower is more equal.",
      reform: r.top_decile_share,
      baseline: b.top_decile_share,
      goodWhen: "decreasing",
      ...pctFmt,
    },
    {
      key: "bottom_decile_share",
      name: "Bottom decile income share",
      description:
        "Share of all income going to the lowest-income 10% of people. Higher is more equal.",
      reform: r.bottom_decile_share,
      baseline: b.bottom_decile_share,
      goodWhen: "increasing",
      ...pctFmt,
    },
    {
      key: "s80_s20",
      name: "S80/S20",
      description:
        "Eurostat's quintile-share ratio: the total income of the richest 20% divided by that of the poorest 20%.",
      reform: r.s80_s20,
      baseline: b.s80_s20,
      goodWhen: "decreasing",
      ...ratioFmt,
    },
    {
      key: "p90_p50",
      name: "P90/P50",
      description:
        "Income at the 90th percentile divided by the median — how far the top of the distribution stretches above the middle.",
      reform: r.p90_p50,
      baseline: b.p90_p50,
      goodWhen: "decreasing",
      ...ratioFmt,
    },
    {
      key: "p50_p10",
      name: "P50/P10",
      description:
        "The median income divided by income at the 10th percentile — how far the middle sits above the bottom.",
      reform: r.p50_p10,
      baseline: b.p50_p10,
      goodWhen: "decreasing",
      ...ratioFmt,
    },
  ];

  return (
    <div className="beamm-inequality-indices" data-testid="inequality-indices">
      <IndexTable
        rows={rows}
        isModified={isModified}
        testId="inequality-index-table"
      />
    </div>
  );
}
